/**
 * Defensive URL sanitiser for CMS-driven navigation/footer links.
 *
 * Returns the URL when it's safe, or null when it must be filtered out.
 *
 * Allowed:
 *  - relative paths starting with `/` (excluding /admin and /api)
 *  - `mailto:` and `tel:` schemes
 *  - `https://` URLs
 *  - `#` fragment anchors
 *
 * Blocked:
 *  - any /admin or /api path (admin must never be exposed publicly)
 *  - javascript:, data:, vbscript:, file:, ftp:
 *  - bare strings without scheme/leading slash
 */
const BLOCKED_PATH_PREFIXES = ["/admin", "/api"];
const ALLOWED_SCHEMES = /^(?:https?:|mailto:|tel:)/i;
const BLOCKED_SCHEMES = /^(?:javascript|data|vbscript|file|ftp):/i;

export function sanitizeNavUrl(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Block dangerous schemes outright
  if (BLOCKED_SCHEMES.test(trimmed)) return null;

  // Allow fragment-only links
  if (trimmed.startsWith("#")) return trimmed;

  // Relative path
  if (trimmed.startsWith("/")) {
    const lower = trimmed.toLowerCase();
    for (const blocked of BLOCKED_PATH_PREFIXES) {
      if (lower === blocked || lower.startsWith(blocked + "/") || lower.startsWith(blocked + "?")) {
        return null;
      }
    }
    return trimmed;
  }

  // Allow http/https/mailto/tel — but force https for protocol-bearing URLs
  if (ALLOWED_SCHEMES.test(trimmed)) {
    if (trimmed.toLowerCase().startsWith("http:")) {
      // Coerce to https for safety
      return "https:" + trimmed.slice(5);
    }
    return trimmed;
  }

  return null;
}

/** Returns true when the URL is an internal `/...` path (used for <Link>). */
export function isInternalUrl(url) {
  return typeof url === "string" && url.startsWith("/");
}
